/**
 * Carries the plan view's height scale into the 3D tree (`spec-onboarding-3d-plan-map-lock`, R2.7).
 *
 * `PlanHeightGroup` wraps one floor and scales it in height only, about the floor's own elevation,
 * every frame. The scale is read from the hook's ref, never React state, so the transition does not
 * re-render the tree. plan-scene wraps each floor in one; the test page uses two; the live twin's
 * `TwinScene` wraps its models in one. The live viewer's `MapCanvas` provides a `PlanViewContext`
 * that reports the Edit flight's height (`liveEditFlightScene`, full height at rest), except during a
 * launch arrival, when it grows from `PLAN_REST_HEIGHT_SCALE` to 1 with the flight
 * (`onboarding-persistent-scene-launch-into-live`, R4).
 */
import { createContext, useContext, useRef } from 'react';
import type { ReactNode } from 'react';
import { useFrame } from '@react-three/fiber';
import type { Group } from 'three';
import { scaleAboutHeight } from './planMapLock';
import type { PlanViewState } from './planMapLock';

export interface PlanViewContextValue {
  getHeightScale: () => number;
  getState: () => PlanViewState;
}

const outside: PlanViewContextValue = { getHeightScale: () => 1, getState: () => 'off' };

/** The context's value where no plan view provides one: full height, `off`. */
export const PLAN_VIEW_OUTSIDE = outside;

export const PlanViewContext = createContext<PlanViewContextValue>(outside);

export const usePlanView = () => useContext(PlanViewContext);

export const PlanHeightGroup = ({ baseY, children }: { baseY: number; children: ReactNode }) => {
  const ref = useRef<Group>(null);
  const { getHeightScale } = usePlanView();
  useFrame(() => {
    const g = ref.current;
    if (!g) return;
    const { positionY, scaleY } = scaleAboutHeight(baseY, getHeightScale());
    if (g.position.y !== positionY || g.scale.y !== scaleY) {
      g.position.y = positionY;
      g.scale.y = scaleY;
      g.updateMatrixWorld(true);
    }
  });
  return <group ref={ref}>{children}</group>;
};
